import Immutable from 'immutable'

import { getRecipes } from '../recipes'

import { useSelector } from 'react-redux'
import { useBasketRecipeUUIDs } from '../hooks'
import { useCallback } from 'react'
import { Collection, Recipe, RecipeCoreId, RecipeUUID } from '../types'

type RecipeSet = {
  id: string
  baseRecipe: Recipe
  options: Recipe[]
}

const generateRecipeSetId = (recipes: Recipe[]) => {
  const coreIds = recipes.map((recipe) => recipe.coreId)

  const uniqueIds = new Set(coreIds)
  const sortedIds = Array.from(uniqueIds).sort()

  return `recipe_family_reference_${sortedIds.join(',')}`
}

const getCollectionRecipeSets = (collection: Collection, recipes: Record<RecipeUUID, Recipe>) => {
  return collection.recipes
    .map(({ id, options }): RecipeSet | null => {
      const recipe = recipes[id]
      if (!recipe) {
        return null
      }

      const optionRecipes = options
        .map((option) => recipes[option.id])
        .filter((option): option is Recipe => Boolean(option))

      return {
        id: generateRecipeSetId([recipe, ...optionRecipes]),
        baseRecipe: recipe,
        options: optionRecipes,
      }
    })
    .filter((recipeSet): recipeSet is RecipeSet => recipeSet !== null)
}

type RecipeSetWithSelectedRecipe = {
  recipeSet: RecipeSet
  selectedRecipe: Recipe
}
/**
 * @description If recipe is not in stock, select first option that is in stock
 */
const selectAvailableOption =
  (isRecipeAvailableOrInBasket: (id: RecipeUUID) => boolean) =>
  (recipeSet: RecipeSet): RecipeSetWithSelectedRecipe => {
    // if recipe is in stock - no further processing
    if (isRecipeAvailableOrInBasket(recipeSet.baseRecipe.id)) {
      return {
        recipeSet: recipeSet,
        selectedRecipe: recipeSet.baseRecipe,
      }
    }

    const availableOptions = recipeSet.options.filter((option) =>
      isRecipeAvailableOrInBasket(option.id),
    )
    // no alternatives in stock - nothing to replace with
    if (availableOptions.length === 0) {
      return {
        recipeSet: recipeSet,
        selectedRecipe: recipeSet.baseRecipe,
      }
    }

    // found an alternative - replace original recipe with it
    return {
      recipeSet: recipeSet,
      selectedRecipe: availableOptions[0],
    }
  }

/**
 * @description Put out of stock recipes last, keeping the order of the rest
 */
const byRecipeAvailability =
  (isRecipeAvailableOrInBasket: (id: RecipeUUID) => boolean) =>
  (a: RecipeSetWithSelectedRecipe, b: RecipeSetWithSelectedRecipe) => {
    const aIsAvailable = isRecipeAvailableOrInBasket(a.selectedRecipe.id)
    const bIsAvailable = isRecipeAvailableOrInBasket(b.selectedRecipe.id)

    if (aIsAvailable === bIsAvailable) {
      return 0
    }

    if (aIsAvailable && !bIsAvailable) {
      return -1
    } else {
      return 1
    }
  }

const overwriteUserSelectedRecipeOption =
  (selectedRecipeVariants: Record<string, RecipeCoreId>) => (item: RecipeSetWithSelectedRecipe) => {
    if (!selectedRecipeVariants || !selectedRecipeVariants[item.recipeSet.id]) {
      return item
    }

    const selectedOption = selectedRecipeVariants[item.recipeSet.id] as RecipeCoreId
    if (item.recipeSet.baseRecipe.coreId === selectedOption) {
      return {
        recipeSet: item.recipeSet,
        selectedRecipe: item.recipeSet.baseRecipe,
      }
    }

    const matchingCollectionRecipeOption = item.recipeSet.options.find(
      (option) => option.coreId === selectedOption,
    )
    if (matchingCollectionRecipeOption) {
      return {
        recipeSet: item.recipeSet,
        selectedRecipe: matchingCollectionRecipeOption,
      }
    }

    return item
  }

/**
 * @param selectedRecipeVariants keys are either RecipeCoreIds or recipe set references
 * @param collection
 */
export const useGetRecipesForCollectionId = (
  selectedRecipeVariants: Record<string, RecipeCoreId>,
  collection: Collection | null,
) => {
  const legacyRecipes = useSelector(getRecipes)

  const recipes = useSelector(({ recipesV3 }: any) => recipesV3.recipes) as Record<
    RecipeUUID,
    Recipe
  >

  const basketRecipeUUIDs = useBasketRecipeUUIDs()
  const isRecipeAvailableOrInBasket = useCallback(
    (recipeId: RecipeUUID) => {
      return recipes[recipeId]?.isAvailable || basketRecipeUUIDs.has(recipeId)
    },
    [recipes, basketRecipeUUIDs],
  )

  if (!collection) {
    return {
      recipes: Immutable.List<{
        recipe: Immutable.Map<string, string>
        recipeV3: Recipe
        originalId: string
        reference: string
      }>(),
    }
  }

  const selectedRecipes = getCollectionRecipeSets(collection, recipes)
    .map(selectAvailableOption(isRecipeAvailableOrInBasket))
    .sort(byRecipeAvailability(isRecipeAvailableOrInBasket))
    .map(overwriteUserSelectedRecipeOption(selectedRecipeVariants))

  // @TODO We still return "immutable"-wrapped data in this ticket, later it will be replaced to return new v3 Recipe type
  return {
    recipes: Immutable.List<{
      recipe: Immutable.Map<string, string>
      recipeV3: Recipe
      originalId: string
      reference: string
    }>(
      selectedRecipes
        .map(({ recipeSet, selectedRecipe }) => ({
          recipe: legacyRecipes.find(
            (other?: Immutable.Map<string, string>) => other?.get('id') === selectedRecipe.coreId,
          ),
          recipeV3: selectedRecipe,
          originalId: recipeSet.baseRecipe.coreId,
          reference: recipeSet.id,
        }))
        .filter(({ recipe }) => Boolean(recipe)),
    ),
  }
}
